import CloseIcon from '@mui/icons-material/Close';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import MedicationIcon from '@mui/icons-material/Medication';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { bulkUnlockItemsFromPhysicalCountApi, listInPhysicalCountItemsApi } from 'api/inventory';
import colors from 'common/constants/colors';
import useRoles from 'common/hooks/useRoles';
import { handleError } from 'store/thunks/errorHandlerThunks';

const formatLockedAt = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const HEADER_CELL_SX = {
  bgcolor: colors.dialogCloseNavy,
  color: '#fff',
  fontWeight: 600,
  fontSize: '0.75rem',
};

const InventoryCountDialog = ({ open, onClose, filter, onUnlockSuccess }) => {
  const dispatch = useDispatch();
  // The banner that opens this dialog is visible to everyone who can open the Inventory
  // dashboard, Viewer included. Reading which items are in a count is fine for all of them;
  // releasing a count from under the counter is not, so the unlock controls carry the same
  // permission as starting one.
  const { performPhysicalCount: canManagePhysicalCount } = useRoles();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [unlocking, setUnlocking] = useState(false);
  const [confirmAnchor, setConfirmAnchor] = useState(null);

  useEffect(() => {
    if (!open) {
      setItems([]);
      setSelectedIds([]);
      return;
    }
    setLoading(true);
    listInPhysicalCountItemsApi({ filter, limit: 100 })
      .then((result) => {
        setItems(result?.items ?? []);
        setLoading(false);
      })
      .catch((error) => {
        dispatch(handleError({ error }));
        setLoading(false);
      });
  }, [open, filter, dispatch]);

  const drugCount = useMemo(() => new Set(items.map((i) => i.ndc)).size, [items]);

  const facilityNames = useMemo(
    () => [...new Set(items.map((i) => i.facilityName))].join(', '),
    [items]
  );

  const allSelected = items.length > 0 && selectedIds.length === items.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < items.length;

  const handleToggleRow = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const handleToggleAll = () => {
    setSelectedIds(allSelected ? [] : items.map((i) => i.inventoryItemId));
  };

  const runUnlock = (ids) => {
    setUnlocking(true);
    bulkUnlockItemsFromPhysicalCountApi(ids)
      .then(() => {
        setUnlocking(false);
        setConfirmAnchor(null);
        setSelectedIds([]);
        onUnlockSuccess();
        onClose();
      })
      .catch((error) => {
        dispatch(handleError({ error }));
        setUnlocking(false);
      });
  };

  const confirmUnlockIds =
    selectedIds.length > 0 ? selectedIds : items.map((i) => i.inventoryItemId);

  const confirmLabel =
    selectedIds.length > 0
      ? `Unlock ${selectedIds.length} selected item${selectedIds.length !== 1 ? 's' : ''}?`
      : `Unlock all ${items.length} item${items.length !== 1 ? 's' : ''}?`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            bgcolor: colors.inventoryLockOrange,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <FactCheckIcon sx={{ fontSize: '1.4rem', color: '#fff' }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant='h6' sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            In Physical Count
          </Typography>
          <Typography variant='caption' sx={{ color: colors.inventoryLockOrange, fontWeight: 600 }}>
            {'• '}
            {drugCount} medication{drugCount !== 1 ? 's' : ''} locked
            {facilityNames && ` · ${facilityNames}`}
          </Typography>
        </Box>
        <IconButton size='small' onClick={onClose}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </Box>
      <Divider />
      <DialogContent sx={{ px: 3, py: 2, minHeight: 350 }}>
        {loading ? (
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 280 }}
          >
            <CircularProgress size={32} sx={{ color: colors.inventoryLockOrange }} />
          </Box>
        ) : (
          <>
            {canManagePhysicalCount && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
                <Button
                  size='small'
                  variant='outlined'
                  disabled={items.length === 0}
                  onClick={(e) => setConfirmAnchor(e.currentTarget)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: colors.inventoryLockOrange,
                    color: colors.inventoryLockOrange,
                    '&:hover': {
                      borderColor: colors.inventoryLockOrange,
                      bgcolor: colors.inventoryLockOrangeLight,
                    },
                  }}
                >
                  {selectedIds.length > 0
                    ? `Unlock Selected (${selectedIds.length})`
                    : 'Unlock All'}
                </Button>

                <Popover
                  open={Boolean(confirmAnchor)}
                  anchorEl={confirmAnchor}
                  onClose={() => !unlocking && setConfirmAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  PaperProps={{ sx: { mt: 0.5, p: 2, width: 296 } }}
                >
                  <Typography variant='body2' sx={{ fontWeight: 600, mb: 0.5 }}>
                    {confirmLabel}
                  </Typography>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ display: 'block', mb: 2 }}
                  >
                    Fulfillment and adjustments will resume for these items.
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button
                      size='small'
                      variant='text'
                      disabled={unlocking}
                      onClick={() => setConfirmAnchor(null)}
                      sx={{ textTransform: 'none' }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size='small'
                      variant='contained'
                      disabled={unlocking}
                      onClick={() => runUnlock(confirmUnlockIds)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        bgcolor: colors.inventoryLockOrange,
                        '&:hover': { bgcolor: '#BF360C' },
                      }}
                    >
                      {unlocking && <CircularProgress size={13} sx={{ mr: 0.75, color: '#fff' }} />}
                      Confirm Unlock
                    </Button>
                  </Box>
                </Popover>
              </Box>
            )}
            <Table size='small'>
              <TableHead>
                <TableRow>
                  {canManagePhysicalCount && (
                    <TableCell padding='checkbox' sx={{ ...HEADER_CELL_SX, borderBottom: 'none' }}>
                      <Checkbox
                        size='small'
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={handleToggleAll}
                        sx={{
                          color: '#fff',
                          '&.Mui-checked': { color: '#fff' },
                          '&.MuiCheckbox-indeterminate': { color: '#fff' },
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell sx={HEADER_CELL_SX}>Drug Name</TableCell>
                  <TableCell sx={HEADER_CELL_SX}>NDC</TableCell>
                  {/*
                    POM-6112 — locking a product puts every NDC stocked under the same productId
                    at that facility into the count, so the list can hold several rows that share
                    a drug name. Product ID sits next to NDC because that is the pair a reader
                    scans to see which rows belong together.
                  */}
                  <TableCell sx={HEADER_CELL_SX}>Product ID</TableCell>
                  <TableCell sx={HEADER_CELL_SX}>Manufacturer</TableCell>
                  <TableCell sx={HEADER_CELL_SX}>Facility</TableCell>
                  <TableCell sx={HEADER_CELL_SX}>On Hand</TableCell>
                  <TableCell sx={HEADER_CELL_SX}>Locked Since</TableCell>
                  <TableCell sx={HEADER_CELL_SX}>Locked By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow
                    key={item.inventoryItemId}
                    selected={selectedIds.includes(item.inventoryItemId)}
                    sx={{ '&.Mui-selected': { bgcolor: `${colors.inventoryLockOrange}0D` } }}
                  >
                    {canManagePhysicalCount && (
                      <TableCell padding='checkbox'>
                        <Checkbox
                          size='small'
                          checked={selectedIds.includes(item.inventoryItemId)}
                          onChange={() => handleToggleRow(item.inventoryItemId)}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 26,
                            height: 26,
                            borderRadius: '50%',
                            bgcolor: `${colors.inventoryLockOrange}20`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <MedicationIcon
                            sx={{ fontSize: '0.85rem', color: colors.inventoryLockOrange }}
                          />
                        </Box>
                        <Box>
                          <Typography variant='body2' sx={{ fontWeight: 500 }}>
                            {item.drugName || '—'}
                          </Typography>
                          {item.strength && (
                            <Typography variant='caption' color='text.secondary'>
                              {item.strength}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2' sx={{ fontFamily: 'monospace' }}>
                        {item.ndc || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2' sx={{ fontFamily: 'monospace' }}>
                        {item.productId || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2'>{item.manufacturer || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2'>{item.facilityName || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2'>{item.quantityOnHand ?? '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2'>{formatLockedAt(item.lockedAt)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2'>{item.lockedByName || '—'}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button
          variant='contained'
          onClick={onClose}
          sx={{
            bgcolor: colors.dialogCloseNavy,
            '&:hover': { bgcolor: colors.dialogCloseNavyHover },
            px: 3,
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

InventoryCountDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  filter: PropTypes.shape({ fulfillingPharmacyId: PropTypes.string }),
  onUnlockSuccess: PropTypes.func.isRequired,
};

InventoryCountDialog.defaultProps = {
  filter: undefined,
};

export default InventoryCountDialog;
